/* eslint-disable max-len */
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import ContainerPainel from 'components/container_painel';
import { Loading } from 'components/login';
import { useAuth } from 'contexts/auth/auth_context';
import { UserPermissionEnum } from 'enum/userPermission.enum';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import useComunidadesRuraisTradicionaisStore from 'stores/forms/comunidades_rurais_tradicionais';
import { FormularioComunidadesFormik } from 'interfaces/formularioComunidades.interface';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ButtonForm from 'components/buttons/button-form';

export const boolToSimNao = (v?: boolean | string | number) => {
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (v === 'S' || v === 'SIM' || v === 1) return 'Sim';
  if (v === 'N' || v === 'NAO' || v === 0) return 'Não';
  return v ?? '—';
};

type AddType = 'assentamentos' | 'comunidades' | undefined;

type LocationState = {
  refresh?: boolean;
  addType?: AddType;
  index?: number;
  item?: Record<string, any>;
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AssentamentoItem = any; // NOSONAR
type ComunidadeItem = any; // NOSONAR

export const Linha = ({ label, value }: { label: string; value?: any }) => (
  <Typography sx={{ fontSize: 13, color: '#000000DE', lineHeight: 1.6 }}>
    <b>{label}:</b> {boolToSimNao(value)}
  </Typography>
);

export const CardItem: React.FC<{
  title: string;
  canEdit?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  children: React.ReactNode;
}> = ({ title, canEdit = true, onEdit, onDelete, children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  return (
    <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 1, mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>

        {canEdit && (
          <Box>
            <Button
              size="small"
              variant="outlined"
              endIcon={<MoreVertIcon fontSize="small" />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ textTransform: 'none', borderRadius: 1, px: 1.5, height: 28 }}
            >
              Ações
            </Button>
            <Menu open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { setAnchorEl(null); onEdit?.(); }}>Editar</MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); onDelete?.(); }}>Excluir</MenuItem>
            </Menu>
          </Box>
        )}
      </Box>
      {children}
    </Paper>
  );
};

export const BlocoSecao: React.FC<{
  titulo: string;
  quantidade: number;
  children: React.ReactNode;
}> = ({ titulo, quantidade, children }) => (
  <Box display="flex" justifyContent="center" mt={3} mb={6}>
    <Box
      px="2rem"
      pb={4}
      sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 1, width: '100%', maxWidth: '1200px' }}
    >
      <Box mt={3} display="flex" alignItems="center" gap={1}>
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontWeight: 500,
            fontSize: '20px',
            lineHeight: '160%',
            letterSpacing: '0.15px',
          }}
        >
          {titulo}:
        </Typography>
        <Chip size="small" label={quantidade} sx={{ height: 22, '& .MuiChip-label': { fontWeight: 600 } }} />
      </Box>
      <Box mt={2}>{children}</Box>
    </Box>
  </Box>
);

// Pure helpers moved to top-level and exported for unit testing
export const norm = (s: any) => (s ?? '').toString().trim().toLowerCase();

export const appendIfNotExists = <T extends Record<string, any>>(
  arr: T[] = [],
  item?: T,
  key?: string
) => {
  if (!item) return arr;
  if (!key) return [...arr, item];
  const exists = arr.some(a => norm(a?.[key]) === norm(item?.[key]));
  return exists ? arr : [...arr, item];
};

export const toStringOrEmpty = (v: unknown) => (v != null ? String(v) : '');

export const replaceAt = <T,>(arr: T[], i: number, val: T) =>
  i >= 0 && i < arr.length ? arr.map((x, idx) => (idx === i ? val : x)) : arr;

export function buildLists(
  response: any,
  addType: AddType,
  newItem: Record<string, any> | undefined,
  index?: number
) {
  const servidorAssentamentos = response.assentamentos ?? [];
  const servidorComunidades = response.comunidades ?? [];

  let assentamentos = servidorAssentamentos;
  let comunidades = servidorComunidades;

  if (addType === 'assentamentos') {
    if (index != null) {
      assentamentos = replaceAt(servidorAssentamentos, index, newItem as any);
    } else {
      assentamentos = appendIfNotExists(servidorAssentamentos, newItem, 'nome_do_assentamento');
    }
  }

  if (addType === 'comunidades') {
    if (index != null) {
      comunidades = replaceAt(servidorComunidades, index, newItem as any);
    } else {
      comunidades = appendIfNotExists(servidorComunidades, newItem, 'nome');
    }
  }

  return { assentamentos, comunidades };
}

export function clearLocationState(locationObj: { pathname: string; search?: string; hash?: string; state?: any }) {
  if (!locationObj.state) return;
  const url = locationObj.pathname + (locationObj.search || '') + (locationObj.hash || '');
  window.history.replaceState({}, '', url);
}

export const isLocationState = (s: unknown): s is LocationState =>
  !!s && typeof s === 'object';

const FormularioComunidadesQuilombolasEditar: React.FC = () => {
  const { uuid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    getComunidadesRuraisTradicionais,
    loading,
    setPartialForm,
    salvarComoCadastrado,
    saveDraftForm
  } = useComunidadesRuraisTradicionaisStore();

  const formik = useFormik({
    initialValues: {
      anoReferencia: (new Date().getFullYear() - 1).toString(),
      assentamentos: [],
      comunidades: []
    } as FormularioComunidadesFormik,
    onSubmit: async (values) => {
      console.log('[EDIT > onSubmit] Disparado! Valores atuais do form:', values);

      if (values.assentamentos.length === 0 && values.comunidades.length === 0) {
        alert('É necessário informar pelo menos um assentamento ou comunidade');
        return;
      }

      try {
        console.log('[EDIT > onSubmit] Persistindo listas na store...');
        setPartialForm('assentamentos', values.assentamentos);
        setPartialForm('comunidades', values.comunidades);

        console.log('[EDIT > onSubmit] Chamando salvarComoCadastrado()...');
        const ok = await salvarComoCadastrado();  // << NÃO cria item novo
        console.log('[EDIT > onSubmit] salvarComoCadastrado() retornou:', ok);

        handleFinalSubmit();
      } catch (err) {
        console.error('[EDIT > onSubmit] Erro ao submeter:', err);
      }
    }

  });

  const handleSalvarComoRascunho = () => {
    const values = formik.values;
    console.log('[EDIT > SalvarRascunho] Valores atuais do form:', values);

    setPartialForm('assentamentos', values.assentamentos);
    setPartialForm('comunidades', values.comunidades);

    setTimeout(async () => {
      console.log('[EDIT > SalvarRascunho] Chamando saveDraftForm()...');
      const ok = await saveDraftForm();
      console.log('[EDIT > SalvarRascunho] saveDraftForm() retornou:', ok);
      alert('Rascunho salvo com sucesso');
      handleFinalSubmit();
    }, 100);
  };

  const usuariosPermitidos = [
    UserPermissionEnum.MUNICIPAL_MANAGER,
    UserPermissionEnum.ADMIN,
  ];
  const isUsuarioPermitido = usuariosPermitidos.includes(
    user?.permission.name as UserPermissionEnum,
  );

  useEffect(() => {
    (async () => {
      console.log('[EDIT] Carregando pacote via getComunidadesRuraisTradicionais...', uuid);
      const response = (await getComunidadesRuraisTradicionais(uuid as string)) as unknown as
        FormularioComunidadesFormik & { ano_referencia_do_questionario: string };
      if (!response) {
        console.warn('[EDIT] getComunidadesRuraisTradicionais returned undefined');
        return;
      }
      console.log('[EDIT] Resposta do GET:', response);

      formik.setValues({
        anoReferencia: response.ano_referencia_do_questionario,
        assentamentos: response.assentamentos,
        comunidades: response.comunidades,
      });
      console.log('[EDIT] formik.setValues aplicado.');
    })();
  }, [uuid]);


  useEffect(() => {
    const raw = location.state;
    if (!isLocationState(raw) || !raw.refresh) return;

    (async () => {
      console.log('[EDIT] Voltou do wizard com state:', raw);
      const response = await getComunidadesRuraisTradicionais(uuid as string);
      if (!response) {
        console.warn('[EDIT] getComunidadesRuraisTradicionais (refresh) returned undefined');
        clearLocationState(location);
        return;
      }

      formik.setValues({
        anoReferencia: toStringOrEmpty(response.ano_referencia_do_questionario),
        assentamentos: response.assentamentos ?? [],
        comunidades: response.comunidades ?? [],
      });
      console.log('[EDIT] Listas recarregadas do servidor (sem merge local).');

      clearLocationState(location);
    })();
  }, [Boolean(isLocationState(location.state) && location.state?.refresh)]);


  const persistirListas = async (nextAssent: AssentamentoItem[], nextCom: ComunidadeItem[]) => {
    console.log('[EDIT] persistirListas ->', { nextAssent, nextCom });
    setPartialForm('assentamentos', nextAssent);
    setPartialForm('comunidades', nextCom);
    const ok = await saveDraftForm();
    console.log('[EDIT] persistirListas -> saveDraftForm retornou:', ok);
  };

  // EDITAR: navega com item, índice e tipo
  const handleEditAssentamento = (idx: number, item: AssentamentoItem) => {
    if (!isUsuarioPermitido) return;
    console.log('[EDIT] Editar Assentamento idx:', idx, 'item:', item);
    navigate(ROUTES.COMUNIDADES_RURAIS_TRADICIONAIS, {
      state: {
        mode: 'edit',
        addType: 'assentamentos',
        index: idx,
        item,
        backTo: location.pathname,
        refresh: false,
      },
    });
  };

  const handleEditComunidade = (idx: number, item: ComunidadeItem) => {
    if (!isUsuarioPermitido) return;
    console.log('[EDIT] Editar Comunidade idx:', idx, 'item:', item);
    navigate(ROUTES.COMUNIDADES_RURAIS_TRADICIONAIS, {
      state: {
        mode: 'edit',
        addType: 'comunidades' as const,
        index: idx,
        item,
        backTo: location.pathname,
        refresh: false,
      },
    });
  };

  const handleDeleteAssentamento = async (idx: number) => {
    if (!isUsuarioPermitido) return;
    console.log('[EDIT] Excluir Assentamento idx:', idx);

    const nextAssent = (formik.values.assentamentos || []).filter((_, i) => i !== idx);
    const nextCom = formik.values.comunidades || [];

    formik.setFieldValue('assentamentos', nextAssent);
    try {
      await persistirListas(nextAssent, nextCom);
    } catch {
      alert('Não foi possível excluir o assentamento. Tente novamente.');
    }
  };

  const handleDeleteComunidade = async (idx: number) => {
    if (!isUsuarioPermitido) return;
    console.log('[EDIT] Excluir Comunidade idx:', idx);

    const nextCom = (formik.values.comunidades || []).filter((_, i) => i !== idx);
    const nextAssent = formik.values.assentamentos || [];

    formik.setFieldValue('comunidades', nextCom);
    try {
      await persistirListas(nextAssent, nextCom);
    } catch {
      alert('Não foi possível excluir a comunidade. Tente novamente.');
    }
  };

  const handleFinalSubmit = () => {
    console.log('[EDIT] handleFinalSubmit -> navegando para PACOTE_FORMULARIOS');
    navigate(ROUTES.PACOTE_FORMULARIOS);
  };

  const { assentamentos, comunidades } = formik.values;

  if (loading) return <Loading />;

  return (
    <ContainerPainel
      pagina='Formulário de Comunidades'
      showBackButton={true}
    >
      <Stack px="4rem" pt="2rem" alignItems="center" textAlign="center">
        <Typography sx={{ mt: 2, mb: 1, fontSize: 24, fontWeight: 400 }}>
          Comunidades
        </Typography>
      </Stack>

      <Box display="flex" justifyContent="center" mt={3} mb={6}>
        <Box
          px="2rem"
          pb={4}
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            boxShadow: 1,
            width: '100%',
            maxWidth: '1200px',
          }}
        >
          <Box mt={3}>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '160%',
                letterSpacing: '0.15px',
                verticalAlign: 'middle',
              }}
            >
              Cadastrar informações de Comunidades
            </Typography>
            <Box mt={2} display="flex" alignItems="center" gap={2}>
              <Typography>Ano referência do Questionário: <b>{new Date().getFullYear() - 1}</b></Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            onClick={() => {
              console.log('[EDIT] Clicou em "adicionar comunidades"');
              // limpar marcadores de edição no store antes de criar
              setPartialForm('current_form_unique_id', null);
              setPartialForm('current_add_type', null);
              setPartialForm('current_index', null);

              navigate(ROUTES.COMUNIDADES_RURAIS_TRADICIONAIS, {
                state: { backTo: location.pathname, mode: 'create', pacoteId: uuid }
              });
            }}
            sx={{
              height: 36,
              borderRadius: "4px",
              textTransform: "uppercase",
              fontSize: "14px",
              fontWeight: 700,
              px: 2.5,
              marginTop: "32px",
              boxShadow: "none",
              bgcolor: "#0048B6",
              color: "#fff",
              "&:hover": { bgcolor: "#0048B6" },
            }}
          >
            adicionar comunidades
          </Button>
        </Box>
      </Box>

      {/* Assentamentos */}
      <BlocoSecao titulo="Assentamentos" quantidade={assentamentos?.length || 0}>
        {assentamentos?.map((item: AssentamentoItem, idx: number) => (
          <CardItem
            key={idx} // NOSONAR
            title={item?.nome_do_assentamento || `Assentamento ${idx + 1}`}
            canEdit={isUsuarioPermitido}
            onEdit={() => handleEditAssentamento(idx, item)}
            onDelete={() => handleDeleteAssentamento(idx)}
          >
            <Linha label="Tipo de assentamento" value={item?.tipo_de_assentamento} />
            <Linha label="Número do PA" value={item?.codigo_do_pa} />
            <Linha label="Quantidade de famílias" value={item?.quantidade_de_familias} />
            <Linha label="Possui energia elétrica" value={item?.possui_energia_eletrica} />
            <Linha label="Possui saneamento básico" value={item?.possui_saneamento_basico} />
            <Linha label="Coleta de lixo" value={item?.coleta_de_lixo} />
            <Linha label="Recebeu manutenção das estradas no último ano" value={item?.recebeu_manutencao_das_estradas_no_ultimo_ano} />
            <Linha label="Possui escolas" value={item?.possui_escolas} />
            <Linha label="Possui água para abastecimento doméstico" value={item?.possui_agua_para_abastecimento_domestico} />
            <Linha label="Possui água para produção agropecuária" value={item?.possui_agua_para_producao_agropecuaria} />
            <Linha label="Tem acesso à internet" value={item?.tem_acesso_internet} />
          </CardItem>
        ))}
        {(!assentamentos || assentamentos.length === 0) && (
          <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Nenhum assentamento cadastrado.</Typography>
        )}
      </BlocoSecao>

      {/* Comunidades Quilombolas */}
      <BlocoSecao titulo="Comunidades Quilombolas" quantidade={comunidades?.length || 0}>
        {comunidades?.map((item: ComunidadeItem, idx: number) => (
          <CardItem
            key={idx} // NOSONAR
            title={item?.nome || item?.name || item?.nome_do_assentamento || `Comunidade ${idx + 1}`}
            canEdit={isUsuarioPermitido}
            onEdit={() => handleEditComunidade(idx, item)}
            onDelete={() => handleDeleteComunidade(idx)}
          >
            <Linha label="Quantidade de famílias" value={item?.qtdFamilias || item?.quantidade_familias || item?.quantidade_de_familias} />
            <Linha label="Possui energia elétrica" value={item?.energiaEletrica || item?.possui_energia_eletrica} />
            <Linha label="Possui saneamento básico" value={item?.saneamentoBasico || item?.possui_saneamento_basico} />
            <Linha label="Coleta de lixo" value={item?.coletaLixo || item?.coleta_de_lixo} />
            <Linha label="Possui escolas" value={item?.possuiEscolas || item?.possui_escolas} />
            <Linha label="Possui água para abastecimento doméstico" value={item?.aguaDomestico || item?.possui_agua_domestico || item?.possui_agua_para_abastecimento_domestico} />
            <Linha label="Possui água para produção agropecuária" value={item?.aguaAgro || item?.possui_agua_agropecuaria || item?.possui_agua_para_producao_agropecuaria} />
            <Linha label="Tem acesso à internet" value={item?.internet || item?.tem_acesso_internet} />
          </CardItem>
        ))}
        {(!comunidades || comunidades.length === 0) && (
          <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Nenhuma comunidade cadastrada.</Typography>
        )}
      </BlocoSecao>

      <ButtonForm
        disabled={!formik.isValid}
        onBack={() => navigate(-1)}
        onConfirmSave={() => {
          formik.handleSubmit();
        }}
        onSaveDraft={handleSalvarComoRascunho}
      />
    </ContainerPainel>
  );
};

export default FormularioComunidadesQuilombolasEditar;
